<script setup lang="ts">
import {computed, onMounted, type PropType, ref, watch} from 'vue';
import {
    CabKPI,
    CabReportItem,
    enum_values,
    KpiStatusEnum,
    KpiStatusEnum_name,
    KpiStatusPrognoseEnum,
    KpiStatusPrognoseEnum_name,
    renderCabPeriod,
    StatusEnum,
    StatusEnum_color,
    WaardeTypeEnum,
} from 'common';
import {downloadFile, jsonBodyFromResponse} from '@/base/utils.js';
import {
    deleteDocumentaryProof,
    getDocumentaryProof,
    readObject,
    updateDocumentaryProof,
    updateObject,
} from '@/base/callBackend';
import WaardeInvul from '@/views/general/WaardeInvul.vue';
import { ElInput, ElMessage, ElOption, ElSelect} from 'element-plus';
import StatusSelector from '@/views/general/StatusSelector.vue';
import RedGreenMarker from '@/views/general/RedGreenMarker.vue';
import {EVENT_BUS} from '@/base/EVENT_BUS.js';
import UploadIcon from '../icons/UploadIcon.vue';
import DeleteIcon from '../icons/DeleteIcon.vue';
import WaardeFixed from '@/views/general/WaardeFixed.vue';
import TracePanel from '@/views/admin/TracePanel.vue';

//===================================================================================================================
const props = defineProps({
    item: {
        type    : Object as PropType<CabReportItem>,
        required: true,
    },
    showOrgTheme: {
        type   : Boolean,
        default: false,
    },
});

const kpi                  = ref<CabKPI>(props.item.kpi[0]);
const theUserValue         = ref<number | undefined>(props.item.reportedValue);
const theCalculatedValue   = computed<number | undefined>(() => props.item.calculatedValue);
const theReportedValue     = computed<number | undefined>(() => theUserValue.value ?? theCalculatedValue.value);
const theDoelwaarde        = ref(props.item.doelwaarde);
const theUitkeringsbedrag  = ref(props.item.uitkeringsbedrag);
const theKpiStatus         = ref<KpiStatusEnum | undefined>(props.item.kpiStatus);
const thePrognose          = ref<number | undefined>(props.item.prognose);
const theKpiStatusPrognose = ref<KpiStatusPrognoseEnum | undefined>(props.item.kpiStatusPrognose);
const statusTarget         = ref<StatusEnum[]>([]);
const showWaardeTrace      = ref<boolean>(false);
const showGehaaldTrace     = ref<boolean>(false);
const documentaryProofName = ref<string | undefined>(props.item.documentaryProofName);

//=================================================================================================
onMounted(async () => {
    statusTarget.value = [props.item.status!];
});

//=================================================================================================
watch(theUserValue, (v) => {
    reportedValueChanged(v);
});
watch(theDoelwaarde, (v) => {
    doelwaardeChanged(v);
});
watch(theUitkeringsbedrag, (v) => {
    uitkeringsbedragChanged(v);
});
watch(theKpiStatus, (v) => {
    kpiStatusValueChanged(v);
});
watch(thePrognose, (v) => {
    prognoseChanged(v);
});
watch(theKpiStatusPrognose, (v) => {
    kpiStatusPrognoseChanged(v);
});
watch(statusTarget, async (nv, ov) => {
    if (ov[0]) {
        const newValue        = nv[0];
        const resultingStatus = await onStatusChange(newValue);
        if (resultingStatus !== newValue) {
            statusTarget.value = [resultingStatus];
        }
    }
});
watch(() => EVENT_BUS.red, (on) => {
    if (props.item.waardeExpressionHasErrors) {
        showWaardeTrace.value = on;
    }
    if (props.item.gehaaldExpressionHasErrors) {
        showGehaaldTrace.value = on;
    }
});
watch(() => EVENT_BUS.green, (on) => {
    if (!props.item.waardeExpressionHasErrors) {
        showWaardeTrace.value = on;
    }
    if (!props.item.gehaaldExpressionHasErrors) {
        showGehaaldTrace.value = on;
    }
});

//=================================================================================================
async function reportedValueChanged(v: number | undefined) {
    if (v !== theUserValue.value || v !== props.item.reportedValue) {
        theUserValue.value       = v;
        props.item.reportedValue = v;
        await onChange();
    }
}

async function kpiStatusValueChanged(v: KpiStatusEnum | undefined) {
    if (v !== theKpiStatus.value || v !== props.item.kpiStatus) {
        theKpiStatus.value   = v;
        props.item.kpiStatus = v;
        await onChange();
    }
}

async function prognoseChanged(v: number | undefined) {
    if (v !== thePrognose.value || v !== props.item.prognose) {
        thePrognose.value   = v;
        props.item.prognose = v;
        await onChange();
    }
}

async function kpiStatusPrognoseChanged(v: KpiStatusPrognoseEnum | undefined) {
    if (v !== theKpiStatusPrognose.value || v !== props.item.kpiStatusPrognose) {
        theKpiStatusPrognose.value   = v;
        props.item.kpiStatusPrognose = v;
        await onChange();
    }
}

function documentaryProofChanged(name?: string) {
    if (name !== documentaryProofName.value || name !== props.item.documentaryProofName) {
        documentaryProofName.value      = name;
        props.item.documentaryProofName = name;
    }
}

function keyupOnTheKpiStatus(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        theKpiStatus.value = undefined;
        event.target.blur();
    }
}

function keyupOnTheKpiStatusPrognose(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        theKpiStatusPrognose.value = undefined;
        event.target.blur();
    }
}

async function onChange() {
    await updateObject(props.item);
    const newItem = await readObject(props.item);
    if (newItem) {
        props.item.calculatedKpiStatus        = newItem?.calculatedKpiStatus;
        props.item.calculatedValue            = newItem?.calculatedValue;
        props.item.gehaaldExpressionHasErrors = newItem?.gehaaldExpressionHasErrors;
        props.item.gehaaldExpressionTraces    = newItem?.gehaaldExpressionTraces;
        props.item.waardeExpressionHasErrors  = newItem?.waardeExpressionHasErrors;
        props.item.waardeExpressionTraces     = newItem?.waardeExpressionTraces;
    }
}

function diffInStatusText(): string {
    if (!props.item.kpiStatus) {
        return '';
    }
    if (!props.item.calculatedKpiStatus) {
        return '';
    }
    if (props.item.calculatedKpiStatus === props.item.kpiStatus) {
        return ``;
    }
    return `handmatig verschilt van berekend`;
}

function diffInStatusClass(): string {
    return diffInStatusText() ? 'text-red-600' : '';
}

function isStatusBerekend(): boolean {
    return !!kpi.value.gehaaldExpression;
}

async function uploadDocumentaryProof(uuid: string) {
    const input         = document.createElement('input');
    input.type          = 'file';
    input.style.display = 'none';
    input.accept        = '.pdf';
    input.addEventListener('change', async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file   = target.files?.[0] ?? null;
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            const res  = await updateDocumentaryProof(uuid, formData);
            const body = await jsonBodyFromResponse(res);
            if (res.ok) {
                ElMessage({
                    message : `Bewijsstuk ${documentaryProofName.value ? 'vervangen' : 'toegevoegd'}.`,
                    type    : 'success',
                    duration: 10000,
                });
                documentaryProofChanged(file.name);
            } else {
                console.error(body.message);
                ElMessage({
                    message : `Upload mislukt: (${body.message})`,
                    type    : 'error',
                    duration: 10000,
                });
            }
        }
        document.body.removeChild(input);
    });
    document.body.appendChild(input);
    input.click();

}

//===================================================================================================================
async function doelwaardeChanged(newValue?: number) {
    props.item.doelwaarde = newValue;
    await onChange();
}

async function uitkeringsbedragChanged(newValue?: number) {
    props.item.uitkeringsbedrag = newValue ?? 0;
    await onChange();
}

async function onStatusChange(newStatus: StatusEnum): Promise<StatusEnum> {
    props.item.status = newStatus;
    await onChange();
    const newItem     = await readObject(props.item);
    props.item.status = newItem.status;
    return newItem.status!;
}

//===================================================================================================================
function dynColor() {
    return 'bg-' + StatusEnum_color(props.item.status);
}

function labelClass(first: boolean = false) {
    const w = first ? 'w-44' : 'w-28';
    return `text-xs text-right ${w} mr-2`;
}

function renderMoment() {
    return renderCabPeriod(props.item.period[0]);
}
</script>

<template>
    <div class="flex flex-row items-start" :class="dynColor()">

        <!--DATE-->
        <div class="backdrop-brightness-90 font-extrabold text-center my-1 mx-1 w-32">
            {{ renderMoment() }}
        </div>

        <div class="flex flex-col w-full my-1 pr-4">

            <div class="flex flex-row font-bold text-gray-700 text-xl w-full pb-3" v-if="props.showOrgTheme">
                <div class="px-3">
                    {{ item.organization[0].name }}
                </div>
                -
                <div class="px-3">
                    {{ item.theme[0].name }}
                </div>
            </div>

            <!--ROW -->
            <div class="flex flex-row items-center w-full my-1" v-if="item.showReportedValue()">

                <!--kpi: gerapporteerde waarde-->
                <div class="flex flex-row items-center">
                    <div :class="labelClass(true)">
                        gerapporteerde waarde
                    </div>
                    <template v-if="kpi.isCalculatedValue">
                        <WaardeInvul
                            v-model:waarde="theReportedValue"
                            :waarde-type="kpi.waardeType"
                            magOnbekend
                            disabled
                        />
                        <div :class="labelClass()">
                            handmatig:
                        </div>
                    </template>
                    <WaardeInvul
                        v-model:waarde="theUserValue"
                        :waarde-type="kpi.waardeType"
                        magOnbekend
                    />
                    <template v-if="kpi.isCalculatedValue">
                        <div :class="labelClass(true)">
                            berekend
                        </div>
                        <WaardeInvul
                            v-model:waarde="theCalculatedValue"
                            :waarde-type="kpi.waardeType"
                            disabled
                        />
                        <RedGreenMarker
                            v-if="item.waardeExpressionTraces"
                            :showRedMarker="item.waardeExpressionHasErrors"
                            :showGreenMarker="!item.waardeExpressionHasErrors"
                            v-on:red="showWaardeTrace = !showWaardeTrace"
                            v-on:green="showWaardeTrace = !showWaardeTrace"
                        />
                    </template>
                </div>
            </div>

            <!--ROW -->
            <div class="flex flex-row items-center w-full my-1"
                 v-if="item.showKpiStatus()||item.showCalculatedKpiStatus()">

                <!--KPI: gerapporteerde status-->
                <div class="flex flex-row items-center" v-if="item.showKpiStatus()">
                    <div :class="labelClass(true)" v-if="isStatusBerekend()">
                        gerapporteerde status
                    </div>
                    <div class="font-bold w-44"
                         :class="diffInStatusClass()"
                         :title="diffInStatusText()">
                        {{ KpiStatusEnum_name(item.kpiStatus ? item.kpiStatus : item.calculatedKpiStatus)
                        }}
                    </div>
                </div>

                <!--KPI: handmatige status-->
                <div class="flex flex-row items-center" v-if="item.showKpiStatus()">
                    <div :class="labelClass()" v-if="isStatusBerekend()">
                        handmatige status
                    </div>
                    <div :class="labelClass(true)" v-else>
                        gerapporteerde status
                    </div>
                    <div class="font-bold">
                        <el-select
                            v-model="theKpiStatus"
                            placeholder="kies een optie"
                            style="min-width: 270px"
                            @keyup="keyupOnTheKpiStatus($event)"
                        >
                            <el-option
                                v-for="status in enum_values(KpiStatusEnum)"
                                :key="status"
                                :label="KpiStatusEnum_name(status)"
                                :value="status"
                            />
                        </el-select>
                    </div>
                </div>

                <!--KPI: berekende status-->
                <div class="flex flex-row items-center" v-if="item.showCalculatedKpiStatus()">
                    <div :class="labelClass()">
                        berekende status
                    </div>
                    <div class="font-bold w-44">
                        {{ KpiStatusEnum_name(item.calculatedKpiStatus) }}
                    </div>
                    <RedGreenMarker
                        v-if="item.gehaaldExpressionTraces"
                        :showRedMarker="item.gehaaldExpressionHasErrors"
                        :showGreenMarker="!item.gehaaldExpressionHasErrors"
                        v-on:red="showGehaaldTrace = !showGehaaldTrace"
                        v-on:green="showGehaaldTrace = !showGehaaldTrace"
                    />
                </div>

            </div>

            <!--ROW: bewijsstuk-->
            <div class="flex flex-row items-center w-full my-1" v-if="item.showDocumentaryProof(true)">
                <div class="flex flex-row items-center">
                    <div :class="labelClass(true)">
                        bewijsstuk
                    </div>
                    <div class="bg-white/40 px-2 py-1">
                        <a v-if="documentaryProofName"
                           href="#"
                           @click.prevent="getDocumentaryProof(item.uuid).then((res)=>downloadFile(res, documentaryProofName!))"
                           class="text-blue-500 hover:text-blue-700">
                            {{ documentaryProofName }}
                        </a>
                        <span v-else>none</span>
                    </div>
                    <DeleteIcon v-if="documentaryProofName"
                                @click.prevent="deleteDocumentaryProof(item.uuid).then(() => documentaryProofChanged(undefined))">
                    </DeleteIcon>
                    <UploadIcon
                        :title="documentaryProofName?'vervang':'bijvoegen'"
                        @click.prevent="uploadDocumentaryProof(item.uuid)">
                    </UploadIcon>
                </div>
            </div>

            <!--ROW -->
            <div class="flex flex-row items-center w-full my-1"
                 v-if="item.showPrognose()">

                <!--kpi: prognose-->
                <div class="flex flex-row items-center">
                    <div :class="labelClass(true)">
                        prognose
                    </div>
                    <WaardeInvul
                        v-model:waarde="thePrognose"
                        :waarde-type="kpi.waardeType"
                        mag-onbekend
                    />
                </div>

                <!--KPI: prognose status-->
                <div class="flex flex-row items-center"
                     v-if="item.showKpiStatusPrognose()">
                    <div :class="labelClass()">
                        met status
                    </div>
                    <div class="font-bold">
                        <div class="">
                            <el-select
                                v-model="theKpiStatusPrognose"
                                placeholder="kies een optie"
                                style="min-width: 270px"
                                @keyup="keyupOnTheKpiStatusPrognose($event)"
                            >
                                <el-option
                                    v-for="status in enum_values(KpiStatusPrognoseEnum)"
                                    :key="status"
                                    :label="KpiStatusPrognoseEnum_name(status)"
                                    :value="status"
                                />
                            </el-select>
                        </div>
                    </div>
                </div>

            </div>

            <!--ROW:opmerking-->
            <div class="flex flex-row items-center w-full my-1">
                <div :class="labelClass(true)">
                    opmerking
                </div>

                <div class="flex-1"
                     title="opmerking">
                    <el-input
                        v-model="item.opmerking"
                        @input="onChange()"
                        type="textarea"
                        :autosize="{ minRows: 1, maxRows: 5 }"
                    />
                </div>
            </div>

            <!--ROW: mitigatie-->
            <div class="flex flex-row items-center w-full my-1"
                 v-if="item.showMitigatie()">
                <div :class="labelClass(true)">
                    mitigatie
                </div>

                <div class="flex-1"
                     title="mitigatie">
                    <el-input
                        v-model="item.mitigatie"
                        @input="onChange()"
                        type="textarea"
                        :autosize="{ minRows: 1, maxRows: 5 }"
                    />
                </div>
            </div>

            <!--ROW: uitkering/vecozo-->
            <div class="flex flex-col items-center w-full my-1 border border-gray-400 py-2"
                 v-if="item.showUitkeringsbedrag(true) || item.showVecozoId(true)">

                <div class="relative w-full">
                    <div class="absolute left-3 -top-4 px-2 whitespace-nowrap text-xs" :class="dynColor()">
                        uitkering
                    </div>
                </div>

                <!--ROW: doelwaarde-->
                <div class="flex flex-row items-center w-full my-1" v-if="item.showDoelwaarde(true)">

                    <!--kpi: doelwaarde-->
                    <div class="flex flex-row items-center">
                        <div :class="labelClass(true)">
                            doelwaarde
                        </div>
                        <div class="font-bold">
                            <WaardeInvul
                                v-model:waarde="theDoelwaarde"
                                :waarde-type="kpi.waardeType"
                            />
                        </div>
                    </div>
                </div>

                <!--ROW: uitkering/vecozo-->
                <div class="flex flex-row items-center w-full my-1"
                     v-if="item.showUitkeringsbedrag(true) || item.showVecozoId(true)">

                    <!--uitkeringsbedrag-->
                    <div class="flex flex-row items-center"
                         v-if="item.showUitkeringsbedrag(true)">
                        <div :class="labelClass(true)">
                            uitkeringsbedrag
                        </div>

                        <div class="font-bold">
                            <WaardeInvul
                                v-model:waarde="theUitkeringsbedrag"
                                :waarde-type="WaardeTypeEnum.bedrag"
                            />
                        </div>
                    </div>

                    <!--vecozo id-->
                    <div class="flex flex-row items-center"
                         v-if="item.showVecozoId(true)">
                        <div :class="labelClass(false)">
                            vecozo id
                        </div>
                        <div class="font-bold">
                            <input
                                class="px-2 py-1 border-1 w-full"
                                v-model="item.vecozoId"
                                type="text"
                                style="min-width: 270px"
                                @input="onChange"
                            />
                        </div>
                    </div>

                </div>

                <!--ROW: uitkerings details-->
                <div class="flex flex-row items-center w-full my-1"
                     v-if="item.uitkeringsbedrag && item.vecozoId && item.aangevraagdBedrag!=undefined && item.toegekendBedrag!=undefined">

                    <div class="flex flex-row items-center">
                        <div :class="labelClass(true)">
                            aangevraagd
                        </div>
                        <WaardeFixed
                            v-model:waarde="item.aangevraagdBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                    </div>
                    <div class="flex flex-row items-center">
                        <div :class="labelClass(false)">
                            toegekend
                        </div>
                        <WaardeFixed
                            v-model:waarde="item.toegekendBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                    </div>
                </div>
            </div>

        </div>

        <div class="m-2">
            <StatusSelector :multi="false"
                            v-model:statusSet="statusTarget"
            />
        </div>
    </div>

    <div class="flex flex-col items-stretch w-full">
        <div class="flex flex-col items-stretch w-full overflow-hidden">
            <TracePanel
                v-if="(kpi.waardeExpression||kpi.isCalculatedValue) && showWaardeTrace"
                title="Waarde"
                :expression="kpi.waardeExpression"
                :trace="item.waardeExpressionTraces"
                :has-errors="item.waardeExpressionHasErrors"
            />
            <TracePanel
                v-if="kpi.gehaaldExpression && showGehaaldTrace"
                title="Gehaald"
                :expression="kpi.gehaaldExpression"
                :trace="item.gehaaldExpressionTraces"
                :has-errors="item.gehaaldExpressionHasErrors"
            />
        </div>
    </div>

</template>
